import { gql } from '@apollo/client';

// ============================================================================
// CALENDAR QUERIES - Centralized GraphQL operations for the calendars module
// ============================================================================

// ==================== CALENDAR QUERIES ====================

/** CalendarView - basic calendar fields with settings and isActive */
// Resolve a /calendars/<slug>/view route param to the canonical calendar id —
// the backend's calendar(id) accepts a Mongo id or a slug.
export const RESOLVE_CALENDAR_SLUG = gql`
  query ResolveCalendarSlug($id: String!) {
    calendar(id: $id) {
      id
      slug
      name
    }
  }
`;

export const GET_CALENDAR = gql`
  query GetCalendar($id: String!) {
    calendar(id: $id) {
      id
      slug
      name
      description
      type
      color
      responsibleOwnerId
      settings {
        timezone
        workingHoursStart
        workingHoursEnd
        workingDays
      }
      isActive
    }
  }
`;

/** CalendarDetails - full calendar fields including stats and notifications */
export const GET_CALENDAR_DETAILS = gql`
  query GetCalendar($id: String!) {
    calendar(id: $id) {
      id
      name
      description
      type
      color
      responsibleOwnerId
      visibility
      isPublic
      publicUrl
      linkedEmailAddressId
      settings {
        timezone
        emailNotifications
        smsNotifications
        defaultReminderMinutes
        workingHoursStart
        workingHoursEnd
        workingDays
      }
      totalEvents
      upcomingEvents
      lastEventAt
      isActive
      createdAt
      updatedAt
    }
  }
`;

/** EditCalendar - calendar fields including booking and ownership settings */
export const GET_CALENDAR_EDIT = gql`
  query GetCalendar($id: String!) {
    calendar(id: $id) {
      id
      name
      description
      type
      color
      responsibleOwnerId
      ownerType
      visibility
      isPublic
      linkedEmailAddressId
      companyId
      projectId
      settings {
        timezone
        defaultReminderMinutes
        workingHoursStart
        workingHoursEnd
        workingDays
      }
      isActive
      allowPublicBooking
      bookingPageSlug
      bookingPageTitle
      bookingPageDescription
      bookingPageLogo
      bookingPageProfilePhoto
      requirePaymentUpfront
      sendBookingConfirmations
      sendBookingReminders
    }
  }
`;

export const GET_CALENDARS_BY_IDS = gql`
  query GetCalendarsByIds($ids: [String!]!) {
    getCalendarsByIds(ids: $ids) {
      id
      name
      description
      type
      color
      responsibleOwnerId
      settings {
        timezone
        workingHoursStart
        workingHoursEnd
        workingDays
      }
      isActive
    }
  }
`;

/** CalendarAccountsAdmin - all calendars with full admin fields */
export const GET_ALL_CALENDARS = gql`
  query GetAllCalendars {
    calendars {
      id
      name
      description
      type
      visibility
      responsibleOwnerId
      ownerType
      linkedEmailAddressId
      companyId
      projectId
      color
      isPublic
      totalEvents
      upcomingEvents
      sharedFromEmail
      sharedFromName
      iCalUrl
      allowPublicBooking
      bookingPageSlug
      settings {
        timezone
        emailNotifications
        smsNotifications
        defaultReminderMinutes
      }
      createdAt
      updatedAt
    }
  }
`;

/** BirdsEyeView - basic calendar list */
export const MY_CALENDARS_QUERY = gql`
  query MyCalendars {
    myCalendars {
      id
      name
      type
      description
      responsibleOwnerId
      linkedEmailAddressId
      createdAt
      updatedAt
      lastEventAt
      eventCount
    }
  }
`;

/** MyCalendars - calendar list with booking fields */
export const MY_CALENDARS_WITH_BOOKING_QUERY = gql`
  query MyCalendars {
    myCalendars {
      id
      name
      type
      description
      visibility
      responsibleOwnerId
      linkedEmailAddressId
      createdAt
      updatedAt
      lastEventAt
      eventCount
      allowPublicBooking
      bookingPageSlug
      shares {
        clientId
        permission
      }
    }
  }
`;

/** LifeGoalsTimeline - minimal calendar list for finding Life Goals calendar */
export const GET_MY_CALENDARS = gql`
  query GetMyCalendars {
    myCalendars {
      id
      name
      type
    }
  }
`;

// ==================== CALENDAR EVENT QUERIES ====================

/** CalendarView - events with attendees, iCal, and metadata */
export const GET_CALENDAR_EVENTS = gql`
  query GetCalendarEvents($calendarId: String!, $startDate: String, $endDate: String) {
    calendarEvents(calendarId: $calendarId, startDate: $startDate, endDate: $endDate) {
      id
      calendarId
      title
      description
      startTime
      endTime
      isAllDay
      status
      color
      categories
      location {
        name
        address
      }
      attendees {
        clientId
        email
        name
        status
      }
      isCancelled
      isInboundICalInvite
      # Who actually holds an invite. Distinguishes a real iCal invite from a
      # plain event that merely lists guests, which is what decides whether the
      # editor offers to send an update at all.
      invitedEmails
      # What each dispatch actually did — the view modal reports THIS ("sent to
      # 5 · why nobody was told") instead of asserting a send it never checked.
      lastInviteDispatch {
        method
        sequence
        sentAt
        sentCount
        failedCount
        recipients
        reason
      }
      inviteDispatchHistory {
        method
        sequence
        sentAt
        sentCount
        failedCount
        recipients
        reason
      }
      iCalMethod
      iCalResponseStatus
      iCalOrganizerName
      iCalOrganizerEmail
      conference {
        url
        provider
      }
      reminders {
        minutesBefore
        method
        enabled
      }
      metadata
    }
  }
`;

/** CalendarDetails - events with full location, recurrence, reminders */
export const GET_CALENDAR_EVENTS_DETAILED = gql`
  query GetCalendarEvents($calendarId: String!, $startDate: String, $endDate: String) {
    calendarEvents(calendarId: $calendarId, startDate: $startDate, endDate: $endDate) {
      id
      calendarId
      title
      description
      startTime
      endTime
      isAllDay
      status
      color
      location {
        name
        address
        latitude
        longitude
        room
        floor
        instructions
      }
      recurrence {
        frequency
        interval
        count
        until
        byDay
        byMonth
        byMonthDay
        rruleString
      }
      attendees {
        clientId
        email
        name
        status
      }
      reminders {
        method
        minutesBefore
      }
      isCancelled
      cancellationReason
      metadata
    }
  }
`;

export const GET_MULTI_CALENDAR_EVENTS = gql`
  query GetMultiCalendarEvents($calendarIds: [String!]!, $startDate: String, $endDate: String) {
    multiCalendarEvents(calendarIds: $calendarIds, startDate: $startDate, endDate: $endDate) {
      id
      calendarId
      title
      description
      startTime
      endTime
      isAllDay
      status
      color
      categories
      location {
        name
        address
      }
      attendees {
        clientId
        email
        name
        status
      }
      isCancelled
      isInboundICalInvite
      # Who actually holds an invite. Distinguishes a real iCal invite from a
      # plain event that merely lists guests, which is what decides whether the
      # editor offers to send an update at all.
      invitedEmails
      # See GET_CALENDAR_EVENTS — the view modal reports what each dispatch
      # actually did.
      lastInviteDispatch {
        method
        sequence
        sentAt
        sentCount
        failedCount
        recipients
        reason
      }
      inviteDispatchHistory {
        method
        sequence
        sentAt
        sentCount
        failedCount
        recipients
        reason
      }
      iCalMethod
      iCalResponseStatus
      iCalOrganizerName
      iCalOrganizerEmail
      metadata
    }
  }
`;

/** LifeGoalsTimeline - events with metadata for life goals */
export const GET_LIFE_GOALS = gql`
  query GetLifeGoals($calendarId: String!, $startDate: String!, $endDate: String!) {
    calendarEvents(
      calendarId: $calendarId
      startDate: $startDate
      endDate: $endDate
    ) {
      id
      title
      description
      startTime
      endTime
      metadata
      calendarId
      organizerId
    }
  }
`;

// ==================== EVENT MUTATIONS ====================

export const CREATE_EVENT = gql`
  mutation CreateEvent($input: CalendarEventInput!, $sendInvites: Boolean) {
    createEvent(input: $input, sendInvites: $sendInvites) {
      id
      title
      startTime
      endTime
      color
      categories
      type
      conference {
        provider
        url
      }
    }
  }
`;

/** CalendarView / EventModal - update event with basic return fields */
/**
 * EventModal / drag-resize save.
 *
 * `notifyAttendees` is the answer to "send an updated invite to attendees?".
 * Omitting it keeps the backend's legacy automatic behaviour, so every other
 * caller of this mutation is unaffected; the editor sends an explicit true/false.
 * `lastInviteDispatch` comes back so the toast can report what actually
 * happened — how many were told, or why nobody was.
 */
export const UPDATE_EVENT = gql`
  mutation UpdateEvent($id: String!, $input: CalendarEventInput!, $notifyAttendees: Boolean) {
    updateEvent(id: $id, input: $input, notifyAttendees: $notifyAttendees) {
      id
      title
      startTime
      endTime
      color
      categories
      invitedEmails
      lastInviteDispatch {
        method
        sequence
        sentAt
        sentCount
        failedCount
        recipients
        reason
      }
      # Returned so Apollo's normalized cache updates the open calendar's copy —
      # reopening the event shows the new dispatch without a refetch.
      inviteDispatchHistory {
        method
        sequence
        sentAt
        sentCount
        failedCount
        recipients
        reason
      }
    }
  }
`;

/** ICalInviteModal - update event returning categories */
export const UPDATE_EVENT_WITH_CATEGORIES = gql`
  mutation UpdateEvent($id: String!, $input: CalendarEventInput!) {
    updateEvent(id: $id, input: $input) {
      id
      categories
    }
  }
`;

/** BookingDetailsView - update event returning metadata */
export const UPDATE_EVENT_METADATA = gql`
  mutation UpdateEvent($id: String!, $input: CalendarEventInput!) {
    updateEvent(id: $id, input: $input) {
      id
      metadata
    }
  }
`;

/** EventModal - cancel event with optional cancelAll for recurring */
// $notifyAttendees is deliberately available but never passed from this module — removing a
// meeting is the safe direction, so an omitted value keeps the backend's default of notifying
// every invite-holder. A future "cancel quietly" affordance would pass false explicitly.
export const CANCEL_EVENT = gql`
  mutation CancelEvent($id: String!, $reason: String, $cancelAll: Boolean, $notifyAttendees: Boolean) {
    cancelEvent(id: $id, reason: $reason, cancelAll: $cancelAll, notifyAttendees: $notifyAttendees)
  }
`;

/** CalendarDetails / ICalInviteModal - cancel event without cancelAll */
export const CANCEL_EVENT_SIMPLE = gql`
  mutation CancelEvent($id: String!, $reason: String) {
    cancelEvent(id: $id, reason: $reason)
  }
`;

/**
 * `attendeesNotified`/`notifyMessage` report the cancellation truthfully.
 * $notifyAttendees is deliberately available but never passed from this module, for the same
 * reason as CANCEL_EVENT above: omitted keeps the backend's default of notifying every
 * invite-holder, which is the safe direction for a deletion. A future "delete quietly"
 * affordance would pass false explicitly.
 */
export const DELETE_EVENT = gql`
  mutation DeleteEvent($id: String!, $notifyAttendees: Boolean) {
    deleteEvent(id: $id, notifyAttendees: $notifyAttendees) {
      success
      message
      attendeesNotified
      notifyMessage
    }
  }
`;

// ==================== LIFE GOAL MUTATIONS ====================

export const CREATE_LIFE_GOAL = gql`
  mutation CreateLifeGoal($input: CalendarEventInput!) {
    createEvent(input: $input) {
      id
      title
      description
      startTime
      endTime
      metadata
      calendarId
    }
  }
`;

export const UPDATE_LIFE_GOAL = gql`
  mutation UpdateLifeGoal($id: String!, $input: CalendarEventInput!) {
    updateEvent(id: $id, input: $input) {
      id
      title
      description
      startTime
      endTime
      metadata
      calendarId
    }
  }
`;

export const DELETE_LIFE_GOAL = gql`
  mutation DeleteLifeGoal($id: String!) {
    deleteEvent(id: $id) {
      success
      message
    }
  }
`;

// ==================== CALENDAR CRUD MUTATIONS ====================

/** NewCalendar - create with basic return fields */
export const CREATE_CALENDAR = gql`
  mutation CreateCalendar($input: BusinessCalendarInput!) {
    createCalendar(input: $input) {
      id
      name
      type
    }
  }
`;

/** CalendarAccountsAdmin - create with more return fields */
export const CREATE_CALENDAR_ADMIN = gql`
  mutation CreateCalendar($input: BusinessCalendarInput!) {
    createCalendar(input: $input) {
      id
      name
      description
      type
      visibility
      color
      responsibleOwnerId
    }
  }
`;

/** EditCalendar - update with basic return (partial input — send only changed fields) */
export const UPDATE_CALENDAR = gql`
  mutation UpdateCalendar($id: String!, $input: UpdateBusinessCalendarInput!) {
    updateCalendar(id: $id, input: $input) {
      id
      name
      type
    }
  }
`;

/** CalendarAccountsAdmin - update with admin-facing fields (partial input) */
export const UPDATE_CALENDAR_ADMIN = gql`
  mutation UpdateCalendar($id: String!, $input: UpdateBusinessCalendarInput!) {
    updateCalendar(id: $id, input: $input) {
      id
      name
      description
      type
      visibility
      color
    }
  }
`;

// ==================== DIRECT PERSON SHARING ====================

/** Share a calendar with one or more people (per-person VIEW/EDIT). */
export const SHARE_CALENDAR_WITH_CLIENTS = gql`
  mutation ShareCalendarWithClients($calendarId: String!, $shares: [CalendarShareInput!]!) {
    shareCalendarWithClients(calendarId: $calendarId, shares: $shares) {
      id
      visibility
      shares {
        clientId
        permission
        sharedBy
        sharedAt
      }
    }
  }
`;

/** Remove one person's access to a calendar. */
export const UNSHARE_CALENDAR = gql`
  mutation UnshareCalendar($calendarId: String!, $clientId: String!) {
    unshareCalendar(calendarId: $calendarId, clientId: $clientId) {
      id
      visibility
      shares {
        clientId
        permission
      }
    }
  }
`;

/** Current person-shares on a calendar, enriched with names for display. */
export const GET_CALENDAR_SHARES = gql`
  query GetCalendarShares($calendarId: String!) {
    calendarShares(calendarId: $calendarId) {
      clientId
      clientName
      clientEmail
      preferredName
      isEmployee
      roleNames
      permission
      sharedBy
      sharedByName
      sharedAt
    }
  }
`;

/** Typeahead people-search — employee/client annotated, nickname-aware, never terminated staff.
 *  workEmail/personalEmail are split so the invite Email Source can show + target each. */
export const CALENDAR_SEARCH_PEOPLE = gql`
  query CalendarSearchPeople($search: String!) {
    calendarSearchPeople(search: $search) {
      clientId
      fName
      lName
      preferredName
      email
      workEmail
      personalEmail
      isEmployee
      roleNames
    }
  }
`;

// ==================== AUDIENCE SHARING (live role/tag rules) ====================

/** Role + tag options for the group-share pickers. */
export const GET_CALENDAR_AUDIENCE_OPTIONS = gql`
  query GetCalendarAudienceOptions {
    calendarAudienceOptions {
      roles {
        id
        name
        color
      }
      tags
    }
  }
`;

/** Who a role/tag share would reach RIGHT NOW — the mandatory pre-share preview. */
export const PREVIEW_CALENDAR_AUDIENCE = gql`
  query PreviewCalendarAudience($kind: CalendarAudienceKind!, $value: String!) {
    previewCalendarAudience(kind: $kind, value: $value) {
      clientId
      fName
      lName
      preferredName
      email
      isEmployee
      roleNames
    }
  }
`;

/** Audience rules currently on a calendar, with live member counts. */
export const GET_CALENDAR_AUDIENCE_SHARES = gql`
  query GetCalendarAudienceShares($calendarId: String!) {
    calendarAudienceShares(calendarId: $calendarId) {
      kind
      value
      label
      permission
      sharedByName
      sharedAt
      memberCount
    }
  }
`;

/** Union preview across several role/tag targets — deduped by person. */
export const PREVIEW_CALENDAR_AUDIENCES = gql`
  query PreviewCalendarAudiences($targets: [CalendarAudienceTargetInput!]!) {
    previewCalendarAudiences(targets: $targets) {
      clientId
      fName
      lName
      preferredName
      email
      isEmployee
      roleNames
    }
  }
`;

/** Share with several groups in one confirm — each becomes its own live rule; people in several groups are notified once. */
export const SHARE_CALENDAR_WITH_AUDIENCES = gql`
  mutation ShareCalendarWithAudiences($calendarId: String!, $targets: [CalendarAudienceTargetInput!]!, $permission: CalendarPermission!) {
    shareCalendarWithAudiences(calendarId: $calendarId, targets: $targets, permission: $permission) {
      id
      visibility
      audienceShares {
        kind
        value
        label
        permission
      }
    }
  }
`;

export const SHARE_CALENDAR_WITH_AUDIENCE = gql`
  mutation ShareCalendarWithAudience($calendarId: String!, $kind: CalendarAudienceKind!, $value: String!, $permission: CalendarPermission!) {
    shareCalendarWithAudience(calendarId: $calendarId, kind: $kind, value: $value, permission: $permission) {
      id
      visibility
      audienceShares {
        kind
        value
        label
        permission
      }
    }
  }
`;

export const UNSHARE_CALENDAR_AUDIENCE = gql`
  mutation UnshareCalendarAudience($calendarId: String!, $kind: CalendarAudienceKind!, $value: String!) {
    unshareCalendarAudience(calendarId: $calendarId, kind: $kind, value: $value) {
      id
      audienceShares {
        kind
        value
      }
    }
  }
`;

// ==================== USER PREFERENCES (pinning) ====================

export const GET_MY_CALENDAR_PREFERENCES = gql`
  query GetMyCalendarPreferences {
    myCalendarPreferences {
      id
      pinnedCalendarIds
    }
  }
`;

export const PIN_CALENDAR = gql`
  mutation PinCalendar($calendarId: String!) {
    pinCalendar(calendarId: $calendarId) {
      id
      pinnedCalendarIds
    }
  }
`;

export const UNPIN_CALENDAR = gql`
  mutation UnpinCalendar($calendarId: String!) {
    unpinCalendar(calendarId: $calendarId) {
      id
      pinnedCalendarIds
    }
  }
`;

export const DELETE_CALENDAR = gql`
  mutation DeleteCalendar($id: String!) {
    deleteCalendar(id: $id)
  }
`;

export const UPLOAD_CALENDAR_PROFILE_PHOTO = gql`
  mutation UploadCalendarProfilePhoto($calendarId: String!, $file: Upload!) {
    uploadCalendarProfilePhoto(calendarId: $calendarId, file: $file) {
      id
      bookingPageProfilePhoto
    }
  }
`;

export const EXPORT_CALENDAR = gql`
  mutation ExportCalendar($calendarId: String!) {
    exportCalendar(calendarId: $calendarId) {
      fileName
      icalData
    }
  }
`;

export const SHARE_CALENDAR = gql`
  mutation ShareCalendar($id: String!, $sharedWithEmail: String!, $permissions: [CalendarPermission!]!) {
    shareCalendar(id: $id, sharedWithEmail: $sharedWithEmail, permissions: $permissions) {
      id
      calendarName
      invitationType
      recipientEmail
      recipientPhone
      shareLink
      notificationSent
      status
      permissions
      expiresAt
    }
  }
`;

// ==================== EXTERNAL CALENDARS ====================

/** CalendarView - external calendars without isActive */
export const GET_EXTERNAL_CALENDARS = gql`
  query GetExternalCalendars {
    getExternalCalendars {
      id
      name
      type
      iCalUrl
      color
      sharedFromName
      sharedFromEmail
      isActive
    }
  }
`;

/** CalendarAccountsAdmin - external calendars with createdAt */
export const GET_EXTERNAL_CALENDARS_ADMIN = gql`
  query GetExternalCalendars {
    getExternalCalendars {
      id
      name
      type
      iCalUrl
      color
      sharedFromName
      sharedFromEmail
      isActive
      createdAt
    }
  }
`;

export const ADD_EXTERNAL_CALENDAR = gql`
  mutation AddExternalCalendar($input: BusinessCalendarInput!) {
    createBusinessCalendar(input: $input) {
      id
      name
      iCalUrl
      color
      type
    }
  }
`;

// ==================== LINKED EMAIL ====================

// Reverse cross-module link (event → email): inbound emails linked to a calendar event.
export const INBOUND_EMAILS_BY_CALENDAR_EVENT = gql`
  query InboundEmailsByCalendarEvent($calendarEventId: ID!) {
    inboundEmailsByCalendarEvent(calendarEventId: $calendarEventId) {
      id
      subject
      from
      fromName
      date
    }
  }
`;

export const GET_LINKED_EMAIL = gql`
  query GetLinkedEmailForCalendar($calendarId: String!, $tenantId: String) {
    getLinkedEmailForCalendar(calendarId: $calendarId, tenantId: $tenantId)
  }
`;

// ==================== GOALS ====================

// The goals board for one Brisbane month bucket ("YYYY-MM") — same selection
// shape as GET_CURRENT_MONTH_GOALS, which it supersedes on CalendarView.
export const GET_GOALS_FOR_MONTH = gql`
  query GetGoalsForMonth($calendarId: String!, $month: String!) {
    goalsForMonth(calendarId: $calendarId, month: $month) {
      id
      title
      description
      category
      period
      status
      month
      startDate
      endDate
      progressPercentage
      currentValue
      targetValue
      currentNumericValue
      targetNumericValue
      checkpoints {
        id
        title
        description
        completed
        completedAt
        completedBy
        order
        dueDate
        assignedTo
        weight
        notes
      }
      color
      icon
      imageCid
      imageUrl
      displayOrder
      kpiMetric
      kpiUnit
      notes
      tags
      parentGoalId
      linkedLifeGoalEventId
      assignedTo
      teamMembers
      createdAt
    }
  }
`;

export const MOVE_GOAL_TO_MONTH = gql`
  mutation MoveGoalToMonth($id: String!, $month: String!) {
    moveGoalToMonth(id: $id, month: $month) {
      id
      month
      startDate
      endDate
    }
  }
`;

export const ROLLOVER_INCOMPLETE_GOALS = gql`
  mutation RolloverIncompleteGoals($calendarId: String!, $fromMonth: String!) {
    rolloverIncompleteGoals(calendarId: $calendarId, fromMonth: $fromMonth) {
      id
      title
      month
    }
  }
`;

export const GET_GOAL_MONTH_COMPARISON = gql`
  query GetGoalMonthComparison($calendarId: String!, $month: String!) {
    goalMonthComparison(calendarId: $calendarId, month: $month) {
      current {
        month
        totalGoals
        completedGoals
        inProgressGoals
        atRiskGoals
        tasksTotal
        tasksDone
        taskCompletionPct
        avgGoalProgress
        perPerson {
          clientId
          goals
          completedGoals
          tasksTotal
          tasksDone
          avgProgress
        }
        goals {
          id
          title
          status
          progressPercentage
        }
      }
      previous {
        month
        totalGoals
        completedGoals
        inProgressGoals
        atRiskGoals
        tasksTotal
        tasksDone
        taskCompletionPct
        avgGoalProgress
        perPerson {
          clientId
          goals
          completedGoals
          tasksTotal
          tasksDone
          avgProgress
        }
        goals {
          id
          title
          status
          progressPercentage
        }
      }
    }
  }
`;

export const GET_CURRENT_MONTH_GOALS = gql`
  query GetCurrentMonthGoals($calendarId: String!) {
    currentMonthGoals(calendarId: $calendarId) {
      id
      title
      description
      category
      period
      status
      month
      startDate
      endDate
      progressPercentage
      currentValue
      targetValue
      currentNumericValue
      targetNumericValue
      checkpoints {
        id
        title
        description
        completed
        completedAt
        completedBy
        order
        dueDate
        assignedTo
        weight
        notes
      }
      color
      icon
      imageCid
      imageUrl
      displayOrder
      kpiMetric
      kpiUnit
      notes
      tags
      parentGoalId
      linkedLifeGoalEventId
      assignedTo
      teamMembers
      createdAt
    }
  }
`;

export const GET_CALENDAR_GOALS = gql`
  query GetCalendarGoals($calendarId: String!, $period: String) {
    calendarGoals(calendarId: $calendarId, period: $period) {
      id
      title
      description
      category
      period
      status
      month
      startDate
      endDate
      progressPercentage
      currentValue
      targetValue
      currentNumericValue
      targetNumericValue
      checkpoints {
        id
        title
        description
        completed
        completedAt
        dueDate
        order
        weight
        notes
      }
      color
      icon
      imageCid
      imageUrl
      displayOrder
      kpiMetric
      kpiUnit
      notes
      tags
      parentGoalId
      linkedLifeGoalEventId
      assignedTo
    }
  }
`;

export const CREATE_GOAL = gql`
  mutation CreateCalendarGoal($input: CalendarGoalInput!) {
    createCalendarGoal(input: $input) {
      id
      title
      progressPercentage
    }
  }
`;

export const UPDATE_GOAL = gql`
  mutation UpdateCalendarGoal($id: String!, $input: CalendarGoalInput!) {
    updateCalendarGoal(id: $id, input: $input) {
      id
      title
      progressPercentage
    }
  }
`;

export const DELETE_GOAL = gql`
  mutation DeleteCalendarGoal($id: String!) {
    deleteCalendarGoal(id: $id)
  }
`;

export const UPDATE_CHECKPOINT = gql`
  mutation UpdateGoalCheckpoint($input: UpdateGoalCheckpointInput!) {
    updateGoalCheckpoint(input: $input) {
      id
      progressPercentage
      checkpoints {
        id
        completed
      }
    }
  }
`;

export const GET_LIFE_GOAL_LINKED_GOALS = gql`
  query GetLifeGoalLinkedCalendarGoals {
    lifeGoalLinkedCalendarGoals {
      id
      title
      icon
      category
      progressPercentage
      startDate
      endDate
      linkedLifeGoalEventId
    }
  }
`;

export const ADD_GOAL_CHECKPOINT = gql`
  mutation AddGoalCheckpoint($goalId: String!, $checkpoint: CalendarGoalCheckpointInput!) {
    addGoalCheckpoint(goalId: $goalId, checkpoint: $checkpoint) {
      id
      progressPercentage
      checkpoints {
        id
        title
        completed
      }
    }
  }
`;

export const REMOVE_GOAL_CHECKPOINT = gql`
  mutation RemoveGoalCheckpoint($goalId: String!, $checkpointId: String!) {
    removeGoalCheckpoint(goalId: $goalId, checkpointId: $checkpointId) {
      id
      progressPercentage
      checkpoints {
        id
        title
        completed
      }
    }
  }
`;

// ==================== TAGS ====================

export const GET_CALENDAR_TAGS = gql`
  query GetCalendarTags($calendarId: String!) {
    calendarTags(calendarId: $calendarId) {
      id
      name
      color
      description
      usageCount
    }
  }
`;

/** TagManager - upsert tag with usageCount */
export const UPSERT_CALENDAR_TAG = gql`
  mutation UpsertCalendarTag($calendarId: String!, $input: CalendarTagInput!) {
    upsertCalendarTag(calendarId: $calendarId, input: $input) {
      id
      name
      color
      description
      usageCount
    }
  }
`;

/** TagSelector - upsert tag without usageCount */
export const UPSERT_CALENDAR_TAG_BASIC = gql`
  mutation UpsertCalendarTag($calendarId: String!, $input: CalendarTagInput!) {
    upsertCalendarTag(calendarId: $calendarId, input: $input) {
      id
      name
      color
      description
    }
  }
`;

/** Backend signature is deleteCalendarTag(tagId) — the old (calendarId, name)
    form never matched the schema, so tag deletion was silently broken (2026-07-13 fix). */
export const DELETE_CALENDAR_TAG = gql`
  mutation DeleteCalendarTag($tagId: String!) {
    deleteCalendarTag(tagId: $tagId)
  }
`;

export const SUGGEST_TAG_COLORS = gql`
  query SuggestTagColors {
    suggestTagColors
  }
`;

// ==================== CLIENTS ====================

/** EventModal - clients with tags */
export const GET_CLIENTS_WITH_TAGS = gql`
  query GetClientsWithTags {
    searchClients(search: "") {
      id
      fName
      lName
      businessName
      email
      phoneNumber
      tags
    }
  }
`;

/** GoalsModal - clients with businessName */
export const GET_CLIENTS = gql`
  query GetClients {
    clients {
      id
      fName
      lName
      email
      businessName
    }
  }
`;

/** CalendarView - clients for goals (fName, lName, businessName) */
export const GET_CLIENTS_FOR_GOALS = gql`
  query GetClients {
    clients {
      id
      fName
      lName
      businessName
    }
  }
`;

/** CalendarAccountsAdmin - clients with phone */
export const GET_CLIENTS_ADMIN = gql`
  query GetClients {
    clients {
      id
      fName
      lName
      email
      phoneNumber
    }
  }
`;

// ==================== USER / AUTH ====================

/** EventModal - get current user's tenant */
export const GET_ME = gql`
  query GetMe {
    me {
      id
      tenantId
      fName
      lName
    }
  }
`;

/** MyCalendars - get current user details */
export const ME_QUERY = gql`
  query Me {
    me {
      id
      fName
      lName
      email
    }
  }
`;

export const GET_TENANT_SMS_CONFIG = gql`
  query GetTenantSmsConfig($id: ID!) {
    tenant(id: $id) {
      id
      alphaId
      dedicatedNumber
      useAlphaId
    }
  }
`;

// ==================== BOOKABLE EVENT TYPES ====================

export const BOOKABLE_EVENT_TYPES_QUERY = gql`
  query BookableEventTypes($calendarId: String!) {
    bookableEventTypes(calendarId: $calendarId) {
      id
      calendarId
      name
      description
      color
      icon
      durationMinutes
      isPaid
      price
      currency
      stripePriceId
      isActive
      maxBookingsPerDay
      minNoticeHours
      maxFutureDays
      bufferBeforeMinutes
      bufferAfterMinutes
      locationType
      location
      meetingLink
      autoGenerateMeetingLink
      questions {
        id
        question
        required
        type
        options
      }
      confirmationMessage
      reminderHoursBefore
      displayOrder
      totalBookings
      lastBookedAt
      createdAt
    }
  }
`;

export const CREATE_BOOKABLE_EVENT_TYPE = gql`
  mutation CreateBookableEventType($input: CreateBusinessCalendarBookableEventTypeInput!) {
    createBookableEventType(input: $input) {
      id
      name
      durationMinutes
      isPaid
      price
      isActive
    }
  }
`;

export const UPDATE_BOOKABLE_EVENT_TYPE = gql`
  mutation UpdateBookableEventType($id: String!, $input: UpdateBusinessCalendarBookableEventTypeInput!) {
    updateBookableEventType(id: $id, input: $input) {
      id
      name
      durationMinutes
      isPaid
      price
      isActive
    }
  }
`;

export const DELETE_BOOKABLE_EVENT_TYPE = gql`
  mutation DeleteBookableEventType($id: String!) {
    deleteBookableEventType(id: $id)
  }
`;

export const TOGGLE_EVENT_TYPE_STATUS = gql`
  mutation ToggleBookableEventTypeStatus($id: String!) {
    toggleBookableEventTypeStatus(id: $id) {
      id
      isActive
    }
  }
`;

// ==================== AVAILABILITY ====================

export const GET_CALENDAR_AVAILABILITY = gql`
  query CalendarAvailability($calendarId: String!) {
    calendarAvailability(calendarId: $calendarId) {
      id
      calendarId
      slots {
        dayOfWeek
        startTime
        endTime
      }
      blockedDates
      extraAvailableDates
      timezone
    }
  }
`;

export const CREATE_AVAILABILITY = gql`
  mutation CreateBusinessCalendarAvailability($input: CreateBusinessCalendarAvailabilityInput!) {
    createBusinessCalendarAvailability(input: $input) {
      id
      calendarId
      slots {
        dayOfWeek
        startTime
        endTime
      }
      blockedDates
      timezone
    }
  }
`;

export const UPDATE_AVAILABILITY = gql`
  mutation UpdateBusinessCalendarAvailability($calendarId: String!, $input: UpdateBusinessCalendarAvailabilityInput!) {
    updateBusinessCalendarAvailability(calendarId: $calendarId, input: $input) {
      id
      calendarId
      slots {
        dayOfWeek
        startTime
        endTime
      }
      blockedDates
      timezone
    }
  }
`;

// ==================== PUBLIC BOOKING ====================

/** PublicBookingPage - calendar with full booking page fields */
export const PUBLIC_CALENDAR_QUERY = gql`
  query PublicCalendar($slug: String!) {
    publicCalendar(slug: $slug) {
      id
      name
      description
      color
      bookingPageSlug
      bookingPageTitle
      bookingPageDescription
      bookingPageLogo
      bookingPageProfilePhoto
    }
  }
`;

/** PublicBookingEventPage - calendar with basic booking fields */
export const PUBLIC_CALENDAR_BASIC_QUERY = gql`
  query PublicCalendar($slug: String!) {
    publicCalendar(slug: $slug) {
      id
      name
      description
      color
      bookingPageSlug
      bookingPageTitle
      bookingPageDescription
    }
  }
`;

/** PublicBookingConfirmPage - calendar with minimal booking fields */
export const PUBLIC_CALENDAR_CONFIRM_QUERY = gql`
  query PublicCalendar($slug: String!) {
    publicCalendar(slug: $slug) {
      id
      name
      bookingPageTitle
      bookingPageProfilePhoto
      bookingPageLogo
    }
  }
`;

export const PUBLIC_EVENT_TYPES_QUERY = gql`
  query PublicBookableEventTypes($calendarSlug: String!) {
    publicBookableEventTypes(calendarSlug: $calendarSlug) {
      id
      name
      description
      durationMinutes
      isPaid
      price
      currency
      locationType
      location
      meetingLink
      isActive
    }
  }
`;

/** PublicBookingEventPage - same fields, different operation name */
export const PUBLIC_EVENT_TYPE_QUERY = gql`
  query PublicEventType($calendarSlug: String!) {
    publicBookableEventTypes(calendarSlug: $calendarSlug) {
      id
      name
      description
      durationMinutes
      isPaid
      price
      currency
      locationType
      location
      meetingLink
      isActive
    }
  }
`;

export const AVAILABLE_TIME_SLOTS_QUERY = gql`
  query AvailableTimeSlots(
    $calendarId: String!
    $eventTypeId: String!
    $startDate: String!
    $endDate: String!
  ) {
    availableTimeSlots(
      calendarId: $calendarId
      eventTypeId: $eventTypeId
      startDate: $startDate
      endDate: $endDate
    ) {
      startTime
      endTime
      available
    }
  }
`;

export const CREATE_PUBLIC_BOOKING = gql`
  mutation CreatePublicBooking($input: CreatePublicBookingInput!) {
    createPublicBooking(input: $input) {
      booking {
        id
        title
        startTime
        endTime
      }
      bookingToken
      requiresPayment
      paymentAmount
      paymentCurrency
      stripeCheckoutUrl
    }
  }
`;

// ==================== MANAGE BOOKING ====================

export const GET_BOOKING_BY_TOKEN = gql`
  query GetBookingByToken($token: String!) {
    getBookingByToken(token: $token) {
      id
      title
      startTime
      endTime
      metadata
    }
  }
`;

export const CANCEL_BOOKING = gql`
  mutation CancelBooking($token: String!, $reason: String!) {
    cancelBooking(token: $token, reason: $reason)
  }
`;

// ==================== ICAL / RSVP ====================

export const SEND_RSVP_RESPONSE = gql`
  mutation SendRSVPResponse($eventId: String!, $response: String!, $fromEmail: String) {
    sendRSVPResponse(eventId: $eventId, response: $response, fromEmail: $fromEmail) {
      success
      message
      event {
        id
        iCalResponseStatus
      }
    }
  }
`;

export const EMAIL_SENDERS_QUERY = gql`
  query GetEmailSenders {
    emailSenders
  }
`;

export const USER_EMAIL_ACCOUNTS_QUERY = gql`
  query GetUserEmailAccounts {
    userEmailAccounts {
      id
      email
      provider
      isDefault
      isActive
    }
  }
`;

// ==================== CALENDAR ACCOUNTS ADMIN ====================

// Accepted-email ops removed — the backend retired acceptedEmailAddresses from the
// schema; email→calendar routing is managed via the Email module (EmailAddress.linkedCalendars).

export const TRANSFER_OWNERSHIP = gql`
  mutation TransferCalendarOwnership($calendarId: String!, $newOwnerId: String) {
    transferCalendarOwnership(calendarId: $calendarId, newOwnerId: $newOwnerId) {
      id
      responsibleOwnerId
    }
  }
`;

// ==================== COMPANIES / EMPLOYEES ====================

export const GET_COMPANIES = gql`
  query GetCompanies {
    companies {
      id
      name
    }
  }
`;

export const GET_EMPLOYEES = gql`
  query GetEmployees {
    employees {
      id
      clientId
      workEmail
      client {
        id
        fName
        lName
        email
      }
    }
  }
`;

export const GET_ASSIGNED_EMAIL_ADDRESSES = gql`
  query GetAssignedEmailAddresses {
    emailAddresses {
      email
      name
      type
      associatedClients
    }
  }
`;

// ==================== BOOKING DETAILS VIEW ====================

export const GET_CLIENT_BY_EMAIL = gql`
  query GetClientByEmail($email: String!) {
    clientByEmail(email: $email) {
      id
      fName
      lName
      email
      phoneNumber
    }
  }
`;

export const CREATE_PROJECT = gql`
  mutation CreateProject($input: ProjectInput!) {
    createProject(input: $input) {
      id
      projectName
      projectGoal
      projectDescription
      billingClient {
        id
        fName
        lName
      }
    }
  }
`;

export const GET_PROJECT_BY_ID = gql`
  query GetProject($id: ID!) {
    project(id: $id) {
      id
      projectName
    }
  }
`;

// ==================== OPPORTUNITIES ====================

export const GET_OPPORTUNITIES = gql`
  query GetOpportunities($status: OpportunityStatus, $stage: String) {
    opportunities(status: $status, stage: $stage) {
      id
      title
      description
      clientName
      value
      stage
      status
      priority
      probability
      expectedRevenue
      expectedCloseDate
      assignedToName
    }
  }
`;

// ==================== EVENT TASKS ====================

export const GET_EVENT_TASKS = gql`
  query GetEventTasks($calendarEventId: String!) {
    calendarEventTasks(calendarEventId: $calendarEventId) {
      id
      title
      description
      status
      completed
      completedAt
      completedBy
      dueDate
      order
      assignedTo
      notes
      totalChecklistItems
      completedChecklistItems
      progressPercentage
      checklistItems {
        id
        title
        completed
        completedAt
        completedBy
        order
      }
      createdAt
      updatedAt
    }
  }
`;

export const CREATE_EVENT_TASK = gql`
  mutation CreateEventTask($input: CalendarEventTaskInput!) {
    createCalendarEventTask(input: $input) {
      id
      title
      description
      status
      completed
      completedAt
      dueDate
      order
      assignedTo
      notes
      totalChecklistItems
      completedChecklistItems
      progressPercentage
      checklistItems {
        id
        title
        completed
        completedAt
        order
      }
    }
  }
`;

export const UPDATE_EVENT_TASK = gql`
  mutation UpdateEventTask($id: String!, $input: CalendarEventTaskInput!) {
    updateCalendarEventTask(id: $id, input: $input) {
      id
      title
      description
      status
      completed
      completedAt
      dueDate
      order
      assignedTo
      notes
      totalChecklistItems
      completedChecklistItems
      progressPercentage
      checklistItems {
        id
        title
        completed
        completedAt
        order
      }
    }
  }
`;

export const TOGGLE_TASK_COMPLETION = gql`
  mutation ToggleTaskCompletion($id: String!) {
    toggleCalendarEventTaskCompletion(id: $id) {
      id
      completed
      completedAt
      status
      progressPercentage
    }
  }
`;

export const DELETE_EVENT_TASK = gql`
  mutation DeleteEventTask($id: String!) {
    deleteCalendarEventTask(id: $id)
  }
`;

export const ADD_CHECKLIST_ITEM = gql`
  mutation AddChecklistItem($taskId: String!, $item: CalendarEventTaskChecklistItemInput!) {
    addCalendarEventTaskChecklistItem(taskId: $taskId, item: $item) {
      id
      checklistItems {
        id
        title
        completed
        completedAt
        order
      }
      totalChecklistItems
      completedChecklistItems
      progressPercentage
    }
  }
`;

export const UPDATE_CHECKLIST_ITEM = gql`
  mutation UpdateChecklistItem($input: UpdateCalendarEventTaskChecklistInput!) {
    updateCalendarEventTaskChecklistItem(input: $input) {
      id
      checklistItems {
        id
        title
        completed
        completedAt
        order
      }
      totalChecklistItems
      completedChecklistItems
      progressPercentage
      completed
      status
    }
  }
`;

export const REMOVE_CHECKLIST_ITEM = gql`
  mutation RemoveChecklistItem($taskId: String!, $checklistItemId: String!) {
    removeCalendarEventTaskChecklistItem(taskId: $taskId, checklistItemId: $checklistItemId) {
      id
      checklistItems {
        id
        title
        completed
        order
      }
      totalChecklistItems
      completedChecklistItems
      progressPercentage
    }
  }
`;

export const GET_INCOMPLETE_TASKS = gql`
  query GetIncompleteTasks($calendarId: String) {
    incompleteTasks(calendarId: $calendarId) {
      id
      title
      description
      status
      completed
      dueDate
      calendarEventId
      totalChecklistItems
      completedChecklistItems
      progressPercentage
      checklistItems {
        id
        title
        completed
        order
      }
    }
  }
`;

// ==================== TEST QUERIES ====================
// Queries used ONLY by test files. The old TEST_GET_CALENDAR/TEST_*_EVENT set
// was removed 2026-07-29: it selected fields that do not exist in the schema
// (`events(calendarId:)`) and existed solely so two never-running suites had
// something to mock. Tests now mock the REAL operations above.

/** PublicBookingPage.test - public calendar query without profilePhoto */
export const TEST_PUBLIC_CALENDAR_QUERY = gql`
  query PublicCalendar($slug: String!) {
    publicCalendar(slug: $slug) {
      id
      name
      description
      color
      bookingPageSlug
      bookingPageTitle
      bookingPageDescription
      bookingPageLogo
    }
  }
`;

/** PublicBookingPage.test - same as PUBLIC_EVENT_TYPES_QUERY */
export const TEST_PUBLIC_EVENT_TYPES_QUERY = gql`
  query PublicBookableEventTypes($calendarSlug: String!) {
    publicBookableEventTypes(calendarSlug: $calendarSlug) {
      id
      name
      description
      durationMinutes
      isPaid
      price
      currency
      locationType
      location
      meetingLink
      isActive
    }
  }
`;

/** Draft email + SMS broadcast content using AI from a plain-English goal */
export const DRAFT_BROADCAST_CONTENT = gql`
  mutation DraftBroadcastContent($input: DraftBroadcastInput!) {
    draftBroadcastContent(input: $input) {
      emailSubject
      emailBody
      smsContent
      smsCharCount
      smsOverLimit
    }
  }
`;

/** Refine an existing draft based on user feedback */
export const REFINE_BROADCAST_CONTENT = gql`
  mutation RefineBroadcastContent($input: RefineBroadcastInput!) {
    refineBroadcastContent(input: $input) {
      emailSubject
      emailBody
      smsContent
      smsCharCount
      smsOverLimit
    }
  }
`;

/** "Improve with AI" for a Standard/iCal/Video-meeting event's title + description */
export const PRETTY_UP_EVENT_COPY = gql`
  mutation PrettyUpEventCopy($input: PrettyUpEventCopyInput!) {
    prettyUpEventCopy(input: $input) {
      title
      description
    }
  }
`;

// ── Event ↔ Invoice linking (2026-08-13) ──────────────────
// Module-local copy of bills' `bills` query — never a sibling-module import
// (the older InvoiceLinkPicker.tsx's `../../bills/queries` import is
// documented Known debt in this module's CLAUDE.md, not a pattern to repeat).
export const CALENDAR_LIST_BILLS = gql`
  query CalendarListBills {
    bills {
      id
      status
      isPaid
      currency
      lineItems { id description amount }
      subtotal
      totalAmount
      createdAt
    }
  }
`;

export const LINK_BILL_TO_EVENT = gql`
  mutation LinkBillToEvent($eventId: ID!, $billId: ID) {
    linkBillToEvent(eventId: $eventId, billId: $billId) {
      id
      billId
    }
  }
`;

export const EVENT_BILL_SUMMARY = gql`
  query EventBillSummary($eventId: ID!) {
    eventBillSummary(eventId: $eventId) {
      id
      status
      totalAmount
      currency
      publicUrl
    }
  }
`;

// Module-local copy of clients' `createClient` — never a sibling-module
// import, same convention as CALENDAR_LIST_BILLS above. `ClientInput` is
// already all-optional server-side, so this is the real mutation, not a
// parallel "minimal create" — reusing rather than duplicating.
export const CALENDAR_CREATE_CLIENT = gql`
  mutation CalendarCreateClient($input: ClientInput!) {
    createClient(input: $input) {
      id
      fName
      lName
      email
      phoneNumber
    }
  }
`;
